/** @jsxImportSource @emotion/react */
import {
  AppBar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Toolbar,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import { useLocation } from "react-router-dom";
import { ACCENT } from "../../theme";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import PublicIcon from "@mui/icons-material/Public";
import HandshakeIcon from "@mui/icons-material/Handshake";
import StoreIcon from "@mui/icons-material/Store";
import ConstructionIcon from "@mui/icons-material/Construction";
import MapIcon from "@mui/icons-material/Map";
import { useUser } from "../../hooks/useUser";
import AdminHeader from "../AdminHeader/AdminHeader";
import TAHeader from "../TAHeader/TAHeader";
import { useAccountSwitchListClassesList } from "../../api-client/account-switch/account-switch";
import NotificationsDropdown from "./Notifications/Notifications";
import UserMenu from "./UserMenu/UserMenu";
import AchievementPopup from "../AchievementPopup/AchievementPopup";
import RouterLink from "../RouterLink/RouterLink";
import { useNotifs } from "../../hooks/useNotifs.ts";
import { useNotesList } from "../../api-client/notes/notes";
import { REFRESH_INTERVAL_MS, STALE_TIME } from "../../utils/constants";
import { useGameUpdates } from "../../hooks/useGameUpdates.ts";

// Constants
const HEADER_HEIGHT = 64;
const DRAWER_WIDTH = 240;
// Inline nav links replace the hamburger drawer from this width up. Deliberately
// lower than MUI's md (900px): 800x600 screens are common in classrooms and the
// links fit there once the wordmark is hidden and padding is tightened.
const NAV_UP = "@media (min-width:780px)";

// Types
interface NavigationPage {
  text: string;
  link: string;
  condition: boolean;
  hasBadge: boolean;
  icon: React.ReactNode;
}

const Logo: FC = () => (
  <Box
    sx={{
      borderRadius: "50%",
      backgroundColor: "background.paper",
      height: "32px",
      width: "32px",
    }}
  >
    <img alt="Logo" src="/favicon-32x32.png" height="32" width="32" />
  </Box>
);

interface DrawerContentProps {
  pages: NavigationPage[];
  handleDrawerToggle: () => void;
}

const DrawerContent: FC<DrawerContentProps> = memo(({
  pages,
  handleDrawerToggle
}) => (
  <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
    <Box
      sx={{
        py: 2,
        display: "flex",
        justifyContent: "center",
        backgroundColor: "primary.main",
        color: "primary.contrastText",
      }}
    >
      <Logo />
      <Typography variant="h6" sx={{
        ml: 1
      }}>
        GeoStrat
      </Typography>
    </Box>
    <Divider />
    <List>
      {pages
        .filter((item) => item.condition)
        .map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              sx={{ textAlign: "left" }}
              component={RouterLink}
              to={item.link}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
              {item.hasBadge && (
                <Badge variant="dot" color="secondary" />
              )}
            </ListItemButton>
          </ListItem>
        ))}
    </List>
  </Box>
));

export default function NewHeader() {
  useGameUpdates();
  const user = useUser();
  const notifs = useNotifs();
  const location = useLocation();
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const { data: availableClasses } = useAccountSwitchListClassesList();
  const isTA = !user.isAdmin && (availableClasses?.length ?? 0) > 1;

  const { data: notes } = useNotesList({
    query: {
      refetchInterval: REFRESH_INTERVAL_MS || false,
      staleTime: STALE_TIME,
    },
  });

  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prevState) => !prevState);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prevState) => !prevState);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  // Close admin menu on route change
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  if (location.pathname !== prevPathname) {
    setPrevPathname(location.pathname);
    setMenuOpen(false);
  }

  // Close admin menu on outside click (but allow clicks inside the menu and MUI portals)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const adminButton = document.querySelector('.admin-menu');

      // Don't close if clicking inside the admin menu or the admin button
      const isClickInsideMenu = adminMenuRef.current?.contains(target);
      const isClickOnButton = adminButton?.contains(target);

      // Don't close if clicking inside MUI portals (dropdowns, menus, modals, etc.)
      const isClickInMuiPortal =
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiMenu-root') ||
        target.closest('.MuiModal-root') ||
        target.closest('.MuiDialog-root') ||
        target.closest('[role="presentation"]');

      if (!isClickInsideMenu && !isClickOnButton && !isClickInMuiPortal) {
        closeMenu();
      }
    };

    if (menuOpen) {
      // Use a small delay to avoid closing immediately on open
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [menuOpen, closeMenu]);

  const isCurrentPage = (link: string) =>
    location.pathname === link || location.pathname.startsWith(`${link}/`);

  const pages = useMemo((): NavigationPage[] => {
    const hasUnreadNotes = notes ? notes.some((note) => !note.isRead) : false;

    return [
      {
        text: "News",
        link: "/app/news",
        condition: true,
        hasBadge: false,
        icon: <NewspaperIcon fontSize="small" />
      },
      {
        text: "Nations",
        link: "/app/nations",
        condition: true,
        hasBadge: false,
        icon: <PublicIcon fontSize="small" />
      },
      {
        text: "Diplomacy",
        link: "/app/diplomacy",
        condition: true,
        hasBadge:
          (notifs?.data?.militaryAlliances || 0) > 0 ||
          (notifs?.data?.tradeAlliances || 0) > 0 ||
          (notifs?.data?.wars || 0) > 0 ||
          (notifs?.data?.warnings || 0) > 0 ||
          (notifs?.data?.subpoenas || 0) > 0,
        icon: <HandshakeIcon fontSize="small" />
      },
      {
        text: "Trade",
        link: "/app/trade",
        condition: true,
        hasBadge: (notifs?.data?.trades || 0) > 0,
        icon: <StoreIcon fontSize="small" />
      },
      {
        text: "Development",
        link: "/app/development",
        condition: true,
        hasBadge:
          (notifs?.data?.purchases || 0) > 0 ||
          (notifs?.data?.militaryPurchases || 0) > 0,
        icon: <ConstructionIcon fontSize="small" />
      },
      {
        text: "Map",
        link: "/app/map",
        condition: true,
        hasBadge:
          (notifs?.data?.placements || 0) > 0 ||
          (notifs?.data?.militaryPlacements || 0) > 0 ||
          hasUnreadNotes,
        icon: <MapIcon fontSize="small" />
      },
    ];
  }, [notifs.data, notes]);

  if (!user) {
    return (
      <>
        <Box sx={{ height: `${HEADER_HEIGHT}px` }}></Box>
        <AppBar position="fixed" component="nav" sx={{ height: `${HEADER_HEIGHT}px` }}>
          <Toolbar sx={{ height: `${HEADER_HEIGHT}px` }}>
            <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
            <Skeleton variant="text" width={100} height={40} />
            <Box sx={{ flex: 1 }} />
            <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1 }} />
            <Skeleton variant="circular" width={32} height={32} />
          </Toolbar>
        </AppBar>
      </>
    );
  }

  return (
    <>
      <AchievementPopup />
      <Box sx={{ height: `${HEADER_HEIGHT}px` }}></Box>
      <AppBar position="fixed" component="nav" sx={{ height: `${HEADER_HEIGHT}px` }}>
        <Toolbar sx={{ height: `${HEADER_HEIGHT}px` }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, [NAV_UP]: { display: "none" }, paddingLeft: "24px" }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            component={RouterLink}
            to="/app/news"
            sx={{
              display: { xs: "none", sm: "flex" },
              justifyItems: "center",
              alignItems: "center",
              color: "inherit",
              textDecoration: "none",
            }}
          >
            <Logo />
            <Typography
              variant="h6"
              component="div"
              sx={{
                ml: 1,
                flexGrow: 1,
                display: { xs: "none", md: "block" },
              }}>
              GeoStrat
            </Typography>
          </Box>

          <Box
            sx={{
              display: "none",
              [NAV_UP]: { display: "block" },
              height: "32px",
            }}
          >
            {pages
              .filter((item) => item.condition)
              .map((page) => (
                <Badge
                  variant={"dot"}
                  color="secondary"
                  invisible={!page.hasBadge}
                  key={page.link}
                >
                  <Button
                    to={page.link}
                    component={RouterLink}
                    variant="contained"
                    aria-current={isCurrentPage(page.link) ? "page" : undefined}
                    sx={{
                      // The page you are on gets an accent underline, so the nav says where you are.
                      boxShadow: isCurrentPage(page.link) ? `inset 0 -3px 0 ${ACCENT}` : "none",
                      "&:hover": { boxShadow: isCurrentPage(page.link) ? `inset 0 -3px 0 ${ACCENT}` : "none" },
                      px: { xs: 1.25, md: 2 },
                    }}
                    id={page.text}
                  >
                    {page.text}
                  </Button>
                </Badge>
              ))}
          </Box>
          <Box sx={{ flex: 1, justifyContent: "flex-end", display: "flex" }}>
            {(user.isAdmin || isTA) && (
              <Button
                className={"admin-menu"}
                variant="contained"
                sx={{
                  boxShadow: "none",
                  display: "flex",
                  maxHeight: `${HEADER_HEIGHT}px`,
                  minWidth: 0,
                  maxWidth: { xs: 120, md: 260 },
                }}
                onClick={toggleMenu}
                endIcon={menuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                aria-expanded={menuOpen}
                aria-controls="admin-header-menu"
                aria-label={`Current class: ${user.className.name}. Click to switch classes.`}
              >
                <Box
                  component="span"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {user.className.name}
                </Box>
              </Button>
            )}
            <NotificationsDropdown />
            <UserMenu />
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: "block",
          [NAV_UP]: { display: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: DRAWER_WIDTH },
        }}
      >
        <DrawerContent
          pages={pages}
          handleDrawerToggle={handleDrawerToggle}
        />
      </Drawer>
      {(user.isAdmin || isTA) && (
        <Box
          ref={adminMenuRef}
          id="admin-header-menu"
          sx={{
            position: 'sticky',
            top: `${HEADER_HEIGHT}px`,
            zIndex: 1200,
            opacity: menuOpen ? 1 : 0,
            visibility: menuOpen ? 'visible' : 'hidden',
            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
            pointerEvents: menuOpen ? 'auto' : 'none',
          }}
        >
          {user.isAdmin ? (
            <AdminHeader menuOpen={menuOpen} onClose={closeMenu} />
          ) : (
            <TAHeader menuOpen={menuOpen} onClose={closeMenu} />
          )}
        </Box>
      )}
    </>
  );
}
